import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMeasure } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconTicket } from '@sorare/core/src/atoms/icons/system/iconTicket.ts';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandS, BrandXXL } from '@sorare/core/src/atoms/typography/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import background from '#src/components/series/raffle/RafflePageContent/assets/background.webp';

import { RewardIcons } from '../RaffleEvent/RewardIcons/index.tsx';
import type {
  ClaimAnyRewardMutation,
  ClaimAnyRewardMutationVariables,
  RaffleRewards_anyRewardInterface,
} from './__generated__/index.graphql.ts';
import colorsLogo from './assets/colors-logo.svg';
import paperRipBottom from './assets/paper-rip-bottom.webp';
import paperRipTop from './assets/paper-rip-top.webp';
import videoMov from './assets/video.mov';
import videoWebm from './assets/video.webm';

const CLAIM_ANY_REWARD_MUTATION = gql`
  mutation ClaimAnyRewardMutation(
    $input: claimAnyRewardInput!
    $sport: Sport!
  ) {
    claimAnyReward(input: $input) {
      currentUser {
        slug
        myUnclaimedSetRaffleRewards(sport: $sport) {
          id
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<ClaimAnyRewardMutation, ClaimAnyRewardMutationVariables>;

const PAPERS_ANIMATION_DURATION = 3000;
const VIDEO_START_DELAY = 800;
const REWARD_UNLOCKED_STORY_DELAY = 1600;

const topPaperAnimation = keyframes`
  0% {
    transform: translateY(var(--top-paper-initial-offset));
    animation-timing-function: steps(7);
  }
  30% {
    transform: translateY(var(--top-paper-final-offset));
  }
  70% {
    transform: translateY(var(--top-paper-final-offset));
    animation-timing-function: steps(7);
  }
  100% {
    transform: translateY(var(--top-paper-initial-offset));
  }
`;

const bottomPaperAnimation = keyframes`
  0% {
    transform: translateY(var(--bottom-paper-initial-offset));
    animation-timing-function: steps(7);
  }
  30% {
    transform: translateY(var(--bottom-paper-final-offset));
  }
  70% {
    transform: translateY(var(--bottom-paper-final-offset));
    animation-timing-function: steps(7);
  }
  100% {
    transform: translateY(var(--bottom-paper-initial-offset));
  }
`;

const Root = styled.div`
  height: 100%;
  width: 100%;
  --half-height: var(--root-height) / 2;
  --top-paper-initial-offset: calc(6 * var(--unit) - 100%);
  --top-paper-final-offset: calc(12 * var(--unit) + var(--half-height) - 100%);
  --bottom-paper-initial-offset: calc(-6 * var(--unit) + 100%);
  --bottom-paper-final-offset: calc(
    -12 * var(--unit) - var(--half-height) + 100%
  );

  @media ${laptopAndAbove} {
    height: 0;
    min-height: min(calc(90 * var(--unit)), var(--100vh));
  }
`;
const InitialBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: url(${background}) no-repeat center / cover;
  opacity: 1;

  &.hidden {
    transition: opacity 0.1s linear;
    transition-delay: 0.8s;
    opacity: 0;
  }
`;
const PaperRipsWrapper = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 2;
  pointer-events: none;
`;
const VideoWrapper = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
`;
const StyledVideo = styled.video`
  width: 80%;
  height: 100%;
  object-fit: contain;
  vertical-align: bottom;
`;
const Content = styled(Vertical)`
  position: relative;
  isolation: isolate;
  height: 100%;
  width: 100%;
  padding: calc(8 * var(--unit)) var(--double-unit);
`;
const SBVertical = styled(Vertical)`
  flex: 1;
  min-height: 0;
  justify-content: space-between;
  z-index: 1;
  opacity: 1;
  gap: calc(6 * var(--unit));
  transition: opacity 0.1s linear;
  transition-delay: ${PAPERS_ANIMATION_DURATION / 2}ms;

  &.hidden {
    opacity: 0;
  }
`;
const StyledDiv = styled.div`
  color: var(--c-black);
`;
const PaperRipTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: calc(50% + 12 * var(--unit));
  transform: translateY(var(--top-paper-initial-offset));
  pointer-events: none;
  background: url(${paperRipTop}) no-repeat bottom left / cover;

  &.animate {
    animation: ${topPaperAnimation} ${PAPERS_ANIMATION_DURATION}ms linear
      forwards;
  }
`;
const PaperRipBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: calc(50% + 12 * var(--unit));
  background: url(${paperRipBottom}) no-repeat top right / cover;
  transform: translateY(var(--bottom-paper-initial-offset));
  pointer-events: none;

  &.animate {
    animation: ${bottomPaperAnimation} ${PAPERS_ANIMATION_DURATION}ms linear
      forwards;
  }
`;
const RewardUnlockedStoryWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

type Props = {
  rewards: RaffleRewards_anyRewardInterface[];
};
export const RaffleRewards = ({ rewards }: Props) => {
  const { sport } = useSportContext();
  const [open, setOpen] = useState(true);
  const {
    flags: { disableRaffleRewards = false },
  } = useFeatureFlags();
  const { visual } = useCommonGameSet();
  const [animationState, setAnimationState] = useState<
    | 'beforeAnimation'
    | 'animationStarted'
    | 'animationMidpoint'
    | 'afterAnimation'
  >('beforeAnimation');
  const [rootRef, { height: rootHeight }] = useMeasure<HTMLDivElement>();
  const [claimAnyReward] = useMutation(CLAIM_ANY_REWARD_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const isClaiming = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (animationState === 'animationStarted') {
      const timeout = setTimeout(() => {
        videoRef.current?.play().catch(() => {});
      }, VIDEO_START_DELAY);
      return () => clearTimeout(timeout);
    }
    if (animationState === 'animationMidpoint') {
      const timeout = setTimeout(() => {
        setAnimationState('afterAnimation');
      }, REWARD_UNLOCKED_STORY_DELAY);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [animationState]);

  if (disableRaffleRewards) {
    return null;
  }

  const claimAllRewards = async () => {
    if (isClaiming.current) return;
    isClaiming.current = true;
    await Promise.all(
      rewards.map(async reward =>
        claimAnyReward({
          variables: {
            input: { rewardId: reward.id },
            sport: sport!,
          },
        })
      )
    );
    isClaiming.current = false;
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      hideHeader
      onClose={claimAllRewards}
    >
      <Root
        ref={rootRef}
        style={
          {
            '--root-height': `${rootHeight}px`,
            ...visual.welcomeBackDialog,
          } as unknown as CSSProperties
        }
      >
        <InitialBackground
          className={classNames({
            hidden:
              animationState === 'animationStarted' ||
              animationState === 'animationMidpoint' ||
              animationState === 'afterAnimation',
          })}
          onTransitionEnd={() => {
            setAnimationState('animationMidpoint');
          }}
        />
        <Content>
          <PaperRipsWrapper>
            <PaperRipTop
              className={classNames({
                animate: animationState !== 'beforeAnimation',
              })}
            />
            <PaperRipBottom
              className={classNames({
                animate: animationState !== 'beforeAnimation',
              })}
            />
          </PaperRipsWrapper>
          <VideoWrapper>
            <StyledVideo ref={videoRef} playsInline muted>
              <source
                type="video/quicktime; codecs=hvc1.1.6.H120.b0"
                src={videoMov}
              />
              <source type="video/webm; codecs=vp09.00.41.08" src={videoWebm} />
            </StyledVideo>
          </VideoWrapper>
          {(animationState === 'beforeAnimation' ||
            animationState === 'animationStarted') && (
            <SBVertical
              className={classNames({
                hidden: animationState === 'animationStarted',
              })}
            >
              <Horizontal gap={0.5} center>
                <SorareLogo height={16} />
                <StyledDiv>
                  <img src={colorsLogo} alt="" height={16} />
                </StyledDiv>
              </Horizontal>
              <Vertical center gap={2}>
                <FontAwesomeIcon icon={iconTicket} fontSize={32} />
                <Vertical center gap={1.5} className="text-center">
                  <BrandXXL>
                    <FormattedMessage defaultMessage="It's time!"></FormattedMessage>
                  </BrandXXL>
                  <BrandS>
                    <FormattedMessage defaultMessage="Claim your weekly raffle reward" />
                  </BrandS>
                </Vertical>
              </Vertical>
              <RewardIcons />
              <Button
                size="large"
                color="primary"
                onClick={() => {
                  setAnimationState('animationStarted');
                }}
              >
                <FormattedMessage defaultMessage="Reveal my reward" />
              </Button>
            </SBVertical>
          )}
          {animationState === 'afterAnimation' && (
            <RewardUnlockedStoryWrapper>
              <RewardUnlockedStory
                rewards={rewards}
                hideBackground
                onClose={claimAllRewards}
                useSetBackground
              />
            </RewardUnlockedStoryWrapper>
          )}
        </Content>
      </Root>
    </Dialog>
  );
};

RaffleRewards.fragments = {
  anyRewardInterface: gql`
    fragment RaffleRewards_anyRewardInterface on AnyRewardInterface {
      id
      ...RewardUnlockedStory_reward
    }
    ${RewardUnlockedStory.fragments.anyRewardInterface}
  ` as TypedDocumentNode<RaffleRewards_anyRewardInterface>,
};
